import React, { useEffect, useState } from "react";
import { useOrganizationVendors, usePrimaryOrganizationId } from "../../../../hooks";
import { useUpdateDataRecipients } from "../../../get-compliant/data-recipients/AddDataRecipients";
import {
  CollectionContext,
  OrganizationDataRecipientDto,
} from "../../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { useCustomVendorsAndStorageLocations } from "../../../../util/resources/vendorUtils";
import { QuestionHeading } from "../../../../components/vendor/QuestionHeading";
import { DataRecipientClickThroughTable } from "../../../../components/organisms/data-recipients/DataRecipientClickThroughTable";
import { RemoveVendorDialog } from "../../../../components/dialog/RemoveVendorDialog";
import { VendorSearchBox } from "../../../../components/organisms/vendor/VendorSearchBox";
import { Stack } from "@mui/material";
import { CategorizedVendorInventory } from "../../../../components/organisms/vendor/CategorizedVendorInventory";
import { KNOWN_EMPLOYMENT_VENDOR_CATEGORIES } from "../../../../components/organisms/vendor/VendorCategories";
import { PageLoading } from "../../../../components";
import { Api } from "../../../../../common/service/Api";
import { useHistory } from "react-router";
import { Routes } from "../../../../root/routes";
import { EmployeePageWrapper } from "./EmployeePageWrapper";

const CONTEXT: CollectionContext[] = ["EMPLOYEE"];

export function EmployeeRecipientsPage() {
  const history = useHistory();

  const orgId = usePrimaryOrganizationId();

  const updateDataRecipients = useUpdateDataRecipients(orgId, {
    collectionContext: CONTEXT,
    includeStorageLocations: true,
  });

  const [confirmRemoveVendor, setConfirmRemoveVendor] = useState(
    null as OrganizationDataRecipientDto,
  );

  const [consumerOrgVendors, consumerOrgVendorsRequest] = useOrganizationVendors(
    orgId,
    [],
    true,
    undefined,
    ["CONSUMER"],
  );
  const customVendors = useCustomVendorsAndStorageLocations(consumerOrgVendors);

  const { requests } = updateDataRecipients;

  // add default organizations
  useEffect(() => {
    const doit = async () => {
      await Api.organizationVendor.addDefaultVendors(orgId);
      requests.refresh();
    };

    doit();
  }, [orgId]);

  if (consumerOrgVendorsRequest.running) {
    return <PageLoading />;
  }

  return (
    <EmployeePageWrapper
      selectedTabKey="employee-recipients"
      nextOnClick={() => history.push(Routes.hrCenter.hrDataMap.employeeDisclosures)}
      nextDisabled={updateDataRecipients.enabledVendors.size === 0}
      nextDisabledTooltip="Please add vendors before proceeding to the next step"
    >
      <div style={{ maxWidth: "800px" }}>
        {customVendors.length > 0 && (
          <div className="mt-xl">
            <div className="mb-lg">
              <QuestionHeading
                condensed
                question="Do any of these custom vendors/storage locations collect or store HR data? Click below to add them to your HR data recipient list."
              />
            </div>
            <div className="mb-md">
              <DataRecipientClickThroughTable
                dataRecipients={customVendors}
                handleAddVendor={(dr) =>
                  updateDataRecipients.handleEditedVendor(dr, {
                    name: dr.name,
                    category: dr.category,
                    collectionContext: Array.from(new Set([...dr.collectionContext, "EMPLOYEE"])),
                  })
                }
                handleRemoveVendor={(dr) => {
                  setConfirmRemoveVendor(dr);
                }}
                isAdded={(dr) => updateDataRecipients.enabledVendors.has(dr.vendorId)}
                className="mt-lg"
              />
            </div>
            <RemoveVendorDialog
              open={!!confirmRemoveVendor}
              onClose={() => setConfirmRemoveVendor(null)}
              onAfterRemoved={() => {
                updateDataRecipients.handleRemoveVendor(confirmRemoveVendor?.vendorId);
              }}
              vendorId={confirmRemoveVendor?.vendorId}
              collectionContext={["EMPLOYEE"]}
            />
          </div>
        )}

        <div className="mb-lg">
          <QuestionHeading
            condensed
            question="Search our database to find your remaining Data Recipients. Create a Custom Recipient for any you can’t find. If you use any internal storage systems that are not a vendor, add them as a Custom Data Recipient."
          />
        </div>

        <Stack spacing={3}>
          <VendorSearchBox {...updateDataRecipients} />

          <CategorizedVendorInventory
            {...updateDataRecipients}
            collectionContext={["EMPLOYEE"]}
            categories={KNOWN_EMPLOYMENT_VENDOR_CATEGORIES}
            orgVendors={updateDataRecipients.currentVendors}
            includeStorageLocations={true}
          />
        </Stack>
      </div>
    </EmployeePageWrapper>
  );
}
